// Parse life-total events from a filtered Forge game log into a
// turn-indexed series suitable for charting.
//
// Format notes (verified against real logs — see filterForgeLog docstring):
//   - "Turn: Turn N (Ai(M))" marks the start of each turn; N is turn number,
//     M is which seat (1=Deck A, 2=Deck B) is the active player.
//   - "Life: Life: Ai(N) X > Y" records a life transition. The "Life: Life:"
//     doubling is from Forge's internal log prefix being "Life: " on top of
//     the event label "Life: Ai(...)". Leave it alone; just parse.
//   - Both players start at 20 in Standard. We don't parse starting life
//     out of the log; we initialize and apply deltas.
//
// Life changes that arrive BEFORE any Turn: line (e.g. from a fast-mana
// pre-turn effect) are bucketed into turn 0 so the chart's x-axis starts
// clean. In practice these are rare.

export interface LifePoint {
  /** 0 for pre-first-turn state, then 1, 2, 3, ... as Forge numbers turns. */
  turn: number;
  /** Deck A life after any transitions on this turn. */
  life_a: number;
  /** Deck B life after any transitions on this turn. */
  life_b: number;
}

// Accept both filtered form ("Ai(1)") and raw Forge form with UUID suffix
// ("Ai(1)-a-0c7d15fc-..."). Older matches captured before filterForgeLog
// shipped have raw stdout in playtest_game_logs, so the parser needs to
// handle both shapes to keep historical charts rendering.
const TURN_RE = /^Turn:\s*Turn\s+(\d+)\s*\(Ai\([12]\)/;
// Life lines: "Life: Life: Ai(1) 20 > 19" (filtered)
//          or "Life: Life: Ai(1)-a-<uuid> 20 > 19" (raw)
const LIFE_RE = /^Life:\s*Life:\s*Ai\(([12])\)(?:-[ab]-\S+)?\s+(-?\d+)\s*>\s*(-?\d+)/;

/**
 * Parse one game's filtered log into a life series. The series always
 * includes the starting (20, 20) point at turn 0 so charts have a visible
 * origin. Subsequent points are emitted at every turn boundary where at
 * least one player's life changed since the last emitted point — flat
 * turns collapse, which keeps the chart readable for long games.
 */
export function parseLifeEvents(log: string): LifePoint[] {
  let life_a = 20;
  let life_b = 20;
  let currentTurn = 0;
  const byTurn = new Map<number, LifePoint>();
  byTurn.set(0, { turn: 0, life_a, life_b });

  for (const rawLine of log.split("\n")) {
    const line = rawLine.trimEnd();
    const tm = TURN_RE.exec(line);
    if (tm) {
      currentTurn = Number(tm[1]);
      // Seed this turn's point with current totals so even a turn with no
      // life changes emits a flat point — caller decides whether to thin.
      if (!byTurn.has(currentTurn)) {
        byTurn.set(currentTurn, { turn: currentTurn, life_a, life_b });
      }
      continue;
    }
    const lm = LIFE_RE.exec(line);
    if (lm) {
      const seat = Number(lm[1]);
      const after = Number(lm[3]);
      if (seat === 1) life_a = after;
      else if (seat === 2) life_b = after;
      // Overwrite current turn's point with latest totals so intra-turn
      // deltas collapse to the end-of-turn state for charting.
      byTurn.set(currentTurn, { turn: currentTurn, life_a, life_b });
    }
  }

  // Return in turn order. Keep every turn we saw — even flat ones — so
  // the chart's x-axis is evenly spaced. Consumers can thin if they want.
  return [...byTurn.values()].sort((a, b) => a.turn - b.turn);
}
